import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './GasDistributorClientCard.css';
import AppIcon from './icons/AppIcon';

export default function GasDistributorClientCard({ card, onOpenCard, isDragging }) {
  const [cliente, setCliente] = useState(null);
  const [contato, setContato] = useState(null);
  const [ultimoPedido, setUltimoPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tagsDisponiveis, setTagsDisponiveis] = useState([]);

  useEffect(() => {
    if (card.entity_type === 'gas_distributor_client' && card.entity_id) {
      loadClientData();
    }
  }, [card.entity_id]);

  // Função auxiliar para obter nome da tag pelo ID
  const getTagNameById = (tagId) => {
    const tag = tagsDisponiveis.find(t => t.id === tagId);
    return tag?.nome || tagId;
  };

  const loadClientData = async () => {
    try {
      setLoading(true);

      // Buscar dados do cliente distribuidor
      const { data: clienteData, error: clienteError } = await supabase
        .from('cliente_distribuidor')
        .select('*')
        .eq('id', card.entity_id)
        .single();

      if (clienteError) throw clienteError;
      setCliente(clienteData);

      // Buscar dados do contato
      if (clienteData?.contato_id) {
        const { data: contatoData } = await supabase
          .from('contatos')
          .select('nome, numero')
          .eq('id', clienteData.contato_id)
          .single();
        setContato(contatoData);
      }

      // Buscar último pedido
      const { data: pedidoData } = await supabase
        .from('pedido_gas_distributor')
        .select('id, status, created_at, preco_total, horario_entrega, quantidade')
        .eq('cliente_id', card.entity_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      setUltimoPedido(pedidoData);

      // Buscar tags disponíveis do projeto
      const { data: projectData } = await supabase
        .from('project_items')
        .select('project_id')
        .eq('id', card.id)
        .single();
      
      if (projectData?.project_id) {
        const { data: projectSettings } = await supabase
          .from('projects')
          .select('settings')
          .eq('id', projectData.project_id)
          .single();
        
        if (projectSettings?.settings?.gas_distributor?.tags_configuradas) {
          setTagsDisponiveis(projectSettings.settings.gas_distributor.tags_configuradas);
        }
      }

    } catch (error) {
      console.error('Erro ao carregar dados do cliente:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 13) {
      return `(${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
    }
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPedidoStatus = () => {
    if (!ultimoPedido) return { text: 'Em aberto', class: 'aberto' };
    
    const statusMap = {
      'Confirmado': { text: 'Confirmado', class: 'confirmado' },
      'Agendado': { text: 'Agendado', class: 'agendado' },
      'Em rota': { text: 'Em rota', class: 'em-rota' },
      'Entregue': { text: 'Entregue', class: 'entregue' },
      'Cancelado': { text: 'Cancelado', class: 'cancelado' },
      'Pendente': { text: 'Pendente', class: 'pendente' }
    };
    
    return statusMap[ultimoPedido.status] || { text: ultimoPedido.status, class: 'outro' };
  };

  if (loading) {
    return (
      <div className="gas-client-card loading" style={{ opacity: isDragging ? 0.5 : 1 }}>
        <div className="client-loading">
          <AppIcon name="progress_activity" className="spinning" />
        </div>
      </div>
    );
  }

  const pedidoStatus = getPedidoStatus();

  return (
    <div 
      className="gas-client-card" 
      onClick={() => onOpenCard(card)}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {/* Header com nome e telefone */}
      <div className="client-header">
        <div className="client-avatar">
          <AppIcon name="person" />
        </div>
        <div className="client-info">
          <h4 className="client-name">{contato?.nome || cliente?.tipo_cliente || 'Cliente'}</h4>
          <p className="client-phone">{formatPhoneNumber(contato?.numero)}</p>
        </div>
      </div>

      {/* Status do pedido */}
      <div className="client-pedido">
        <div className="pedido-icon">
          <AppIcon name="local_shipping" />
        </div>
        <div className="pedido-info">
          <span className="pedido-label">Pedido:</span>
          {ultimoPedido?.status === 'Agendado' ? (
            <span className={`pedido-status ${pedidoStatus.class}`}>
              {ultimoPedido?.horario_entrega ? formatDateTime(ultimoPedido.horario_entrega) : 'Agendado'}
            </span>
          ) : (
            <span className={`pedido-status ${pedidoStatus.class}`}>{pedidoStatus.text}</span>
          )}
        </div>
      </div>

      {/* Footer com data de entrada */}
      <div className="client-footer">
        <div className="client-time">
          <AppIcon name="schedule" />
          {formatDateTime(card.created_at)}
        </div>
        {cliente?.tags_desconto?.length > 0 && (
          <div className="client-tags">
            {cliente.tags_desconto.map((tagId, i) => (
              <span key={i} className="client-tag">{getTagNameById(tagId)}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
